import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../providers/ToastProvider';
import {
    patchConversationTriage,
    type ConversationRowRef,
    type ConversationTriagePatch
} from '../services/conversations';
import { apiErrorMessage } from '../utils/apiError';
import {
    applyOptimisticConversationTriage,
    CONVERSATION_TRIAGE_LIST_KEYS,
    restoreConversationTriage,
    snapshotConversationTriage
} from './conversationTriageCache';
import { CONVERSATIONS_INFINITE_KEY, CONVERSATIONS_KEY } from './queryKeys';

interface PatchConversationTriageVariables {
    row: ConversationRowRef;
    patch: ConversationTriagePatch;
}

const SETTLE_INVALIDATION_KEYS = [[CONVERSATIONS_KEY], [CONVERSATIONS_INFINITE_KEY]];

function failureFallback(patch: ConversationTriagePatch): string {
    return patch.priority === undefined
        ? 'Failed to update frustration'
        : 'Failed to update priority';
}

// Optimistic in both list modes; a failure rolls back, and the settle refetch catches up.
export function usePatchConversationTriage() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    return useMutation({
        mutationFn: ({ row, patch }: PatchConversationTriageVariables) =>
            patchConversationTriage(row, patch),
        onMutate: async ({ row, patch }) => {
            await Promise.all(
                CONVERSATION_TRIAGE_LIST_KEYS.map((queryKey) =>
                    queryClient.cancelQueries({ queryKey })
                )
            );
            const snapshot = snapshotConversationTriage(queryClient);
            applyOptimisticConversationTriage(queryClient, row, patch);
            return { snapshot };
        },
        onError: (error, { patch }, context) => {
            if (context) restoreConversationTriage(queryClient, context.snapshot);
            showToast(apiErrorMessage(error, failureFallback(patch)), 'error');
        },
        onSettled: () =>
            Promise.all(
                SETTLE_INVALIDATION_KEYS.map((queryKey) =>
                    queryClient.invalidateQueries({ queryKey })
                )
            )
    });
}
